import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@carrent/ui'
import { carsApi, orgApi, regionsAdminApi } from '@carrent/api'
import { PersonAvatar } from './OrgTree'
import { UserPicker } from '../../tasks/UserPicker'

/**
 * Drawer региона — «провалиться» из дерева, не покидая страницу.
 *
 * Полный состав: ответственный (со сменой), водители (добавить/убрать),
 * машины (перевести сюда/отсюда). Ссылка на полную страницу региона —
 * для остального (финансы, история, документы).
 *
 * Мутации переиспользуют существующие ручки: addUserToRegion,
 * removeUserFromRegion, transferCar. Ничего регионального здесь
 * не изобретается — только собирается в один экран.
 */

const VIA_BADGE = {
  primary:            null, // штатная ситуация — бейдж не нужен
  substitution:       { cls: 'sub', text: 'замещение' },
  escalated_to_owner: { cls: 'esc', text: 'эскалация владельцу' },
}

export default function RegionDrawer({ region, regions, onChanged, onClose }) {
  const navigate = useNavigate()
  const [drivers, setDrivers] = useState(null)
  const [cars, setCars] = useState(null)
  const [err, setErr] = useState('')
  const closeRef = useRef(null)

  const load = () => {
    regionsAdminApi.regionUsers(region.id, 'user')
      .then((r) => setDrivers(r.data?.data || []))
      .catch(() => setErr('Не удалось загрузить водителей'))
    regionsAdminApi.regionCars(region.id)
      .then((r) => setCars(r.data?.data || []))
      .catch(() => setErr('Не удалось загрузить машины'))
  }

  // load пересоздаётся каждый рендер; ключ перезагрузки — регион.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [region.id])

  // Закрытие по Escape; фокус — на кнопку закрытия при открытии.
  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const res = region.responsible || {}
  const badge = VIA_BADGE[res.via]

  return (
    <div className="org-drawer" role="dialog" aria-label={`Регион ${region.name}`}>
      <div className="org-drawer__backdrop" onClick={onClose} />
      <div className="org-drawer__panel">
        <div className="org-drawer__head">
          <span className={`org-region__dot org-region__dot--${region.routing}`} />
          <h2 className="org-drawer__title">{region.name}</h2>
          <span className="org-drawer__code">{region.code}</span>
          <button type="button" className="btn btn--sm"
            onClick={() => navigate(`/admin/regions/${region.id}`)}>
            Страница региона
          </button>
          <button type="button" ref={closeRef} className="org-drawer__close"
            aria-label="Закрыть" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </div>

        {err && <div className="alert alert--error">{err}</div>}

        <ResponsibleSection region={region} badge={badge} onChanged={onChanged} />
        <DriversSection region={region} drivers={drivers}
          onChanged={() => { load(); onChanged() }} />
        <CarsSection region={region} cars={cars} regions={regions}
          onChanged={() => { load(); onChanged() }} />
      </div>
    </div>
  )
}

function ResponsibleSection({ region, badge, onChanged }) {
  const res = region.responsible || {}
  const [editing, setEditing] = useState(false)
  const [manager, setManager] = useState(region.manager || null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const save = async (value) => {
    setErr(''); setSaving(true)
    try {
      await orgApi.setRegionManager(region.id, value ? value.id : null)
      setEditing(false)
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="org-drawer__section">
      <div className="org-drawer__section-head">
        <h3 className="org-h3">Ответственный</h3>
        {!editing && (
          <button className="btn btn--sm" onClick={() => setEditing(true)}>
            Изменить
          </button>
        )}
      </div>

      {!editing && (
        <div className="org-drawer__resp">
          {res.user ? (
            <>
              <PersonAvatar name={res.user.full_name} size="sm" />
              <span>{res.user.full_name}</span>
              {badge && (
                <span className={`org-region__badge org-region__badge--${badge.cls}`}>
                  {badge.text}
                </span>
              )}
            </>
          ) : (
            <span className="text-muted">Не назначен — согласования уходят владельцу</span>
          )}
        </div>
      )}

      {editing && (
        <div className="flex-col org-drawer__edit">
          <p className="text-muted org-modal__hint">
            К этому человеку идут согласования по региону. Если он недоступен
            и замещение не назначено, согласования уходят владельцу.
          </p>
          <UserPicker selected={manager} onChange={setManager}
            placeholder="Найти менеджера..." role="manager" />
          {err && <div className="alert alert--error">{err}</div>}
          <div className="org-subform__actions">
            <button className="btn btn--primary" disabled={saving}
              onClick={() => save(manager)}>
              {saving ? <span className="spinner" /> : 'Сохранить'}
            </button>
            {region.manager && (
              <button className="btn" disabled={saving} onClick={() => save(null)}>
                Снять
              </button>
            )}
            <button className="btn" disabled={saving}
              onClick={() => setEditing(false)}>Отмена</button>
          </div>
        </div>
      )}
    </section>
  )
}

function DriversSection({ region, drivers, onChanged }) {
  const [adding, setAdding] = useState(false)
  const [q, setQ] = useState('')
  const [found, setFound] = useState([])
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!adding) return
    const t = setTimeout(() => {
      regionsAdminApi.regionAvailableUsers(region.id, 'user', q)
        .then((r) => setFound(r.data?.data || []))
        .catch(() => setFound([]))
    }, 250)
    return () => clearTimeout(t)
  }, [adding, q, region.id])

  const add = async (user) => {
    setErr('')
    try {
      await regionsAdminApi.addUserToRegion(region.id, user.id)
      setAdding(false); setQ('')
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось добавить')
    }
  }

  const remove = async (user) => {
    if (!confirm(`Убрать водителя ${user.full_name} из региона «${region.name}»?`)) return
    setErr('')
    try {
      await regionsAdminApi.removeUserFromRegion(region.id, user.id)
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось убрать')
    }
  }

  return (
    <section className="org-drawer__section">
      <div className="org-drawer__section-head">
        <h3 className="org-h3">Водители {drivers ? `(${drivers.length})` : ''}</h3>
        {!adding && (
          <button className="btn btn--sm" onClick={() => setAdding(true)}>
            + Добавить
          </button>
        )}
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      {adding && (
        <div className="org-drawer__search">
          <input className="input" autoFocus value={q} placeholder="Поиск водителя..."
            onChange={(e) => setQ(e.target.value)} />
          <button className="btn btn--sm"
            onClick={() => { setAdding(false); setQ('') }}>Отмена</button>
          <div className="org-drawer__results">
            {found.map((u) => (
              <button type="button" key={u.id} className="org-drawer__result"
                onClick={() => add(u)}>
                <PersonAvatar name={u.full_name} size="xs" />
                <span>{u.full_name}</span>
                <span className="text-muted">{u.phone}</span>
              </button>
            ))}
            {found.length === 0 && (
              <p className="text-muted org-drawer__empty">Никого не найдено</p>
            )}
          </div>
        </div>
      )}

      {drivers === null && <div className="spinner" />}
      {drivers?.length === 0 && !adding && (
        <p className="text-muted">В регионе нет водителей</p>
      )}
      {drivers?.map((u) => (
        <div key={u.id} className="org-drawer__row">
          <PersonAvatar name={u.full_name} size="sm" />
          <div className="org-drawer__row-main">
            <div>{u.full_name}</div>
            <div className="text-muted org-drawer__row-sub">{u.phone}</div>
          </div>
          <button type="button" className="org-drawer__row-x"
            aria-label={`Убрать: ${u.full_name}`} onClick={() => remove(u)}>
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </section>
  )
}

function CarsSection({ region, cars, regions, onChanged }) {
  const [adding, setAdding] = useState(false)
  const [q, setQ] = useState('')
  const [found, setFound] = useState([])
  const [movingCar, setMovingCar] = useState(null) // машина с открытым выбором региона
  const [err, setErr] = useState('')

  const others = regions.filter((r) => r.id !== region.id)

  useEffect(() => {
    if (!adding) return
    const t = setTimeout(() => {
      carsApi.adminList({ q })
        .then((r) => {
          const items = r.data?.data?.items || r.data?.data || []
          setFound(items.filter((c) => c.region_id !== region.id))
        })
        .catch(() => setFound([]))
    }, 250)
    return () => clearTimeout(t)
  }, [adding, q, region.id])

  const transferIn = async (car) => {
    setErr('')
    try {
      await regionsAdminApi.transferCar(car.id, region.id, 'Перевод из структуры компании')
      setAdding(false); setQ('')
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось перевести машину')
    }
  }

  const transferOut = async (car, toRegionId) => {
    setErr('')
    try {
      await regionsAdminApi.transferCar(car.id, Number(toRegionId), 'Перевод из структуры компании')
      setMovingCar(null)
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось перевести машину')
    }
  }

  return (
    <section className="org-drawer__section">
      <div className="org-drawer__section-head">
        <h3 className="org-h3">Машины {cars ? `(${cars.length})` : ''}</h3>
        {!adding && (
          <button className="btn btn--sm" onClick={() => setAdding(true)}>
            + Перевести сюда
          </button>
        )}
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      {adding && (
        <div className="org-drawer__search">
          <input className="input" autoFocus value={q}
            placeholder="Поиск машины (марка, номер)..."
            onChange={(e) => setQ(e.target.value)} />
          <button className="btn btn--sm"
            onClick={() => { setAdding(false); setQ('') }}>Отмена</button>
          <div className="org-drawer__results">
            {found.map((c) => (
              <button type="button" key={c.id} className="org-drawer__result"
                onClick={() => transferIn(c)}>
                <span>{c.brand} {c.model}</span>
                <span className="org-drawer__plate">{c.plate}</span>
                <span className="text-muted">{c.region?.name || 'без региона'}</span>
              </button>
            ))}
            {found.length === 0 && (
              <p className="text-muted org-drawer__empty">Машин в других регионах не найдено</p>
            )}
          </div>
        </div>
      )}

      {cars === null && <div className="spinner" />}
      {cars?.length === 0 && !adding && (
        <p className="text-muted">В регионе нет машин</p>
      )}
      {cars?.map((c) => (
        <div key={c.id} className="org-drawer__row">
          <div className="org-drawer__row-main">
            <div>{c.brand} {c.model}</div>
            <div className="text-muted org-drawer__row-sub">
              <span className="org-drawer__plate">{c.plate}</span>
              {c.status_label ? ` · ${c.status_label}` : ''}
            </div>
          </div>
          {movingCar === c.id ? (
            <select className="input org-drawer__move" defaultValue=""
              onChange={(e) => e.target.value && transferOut(c, e.target.value)}>
              <option value="" disabled>Куда перевести...</option>
              {others.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          ) : (
            <button className="btn btn--sm" disabled={others.length === 0}
              title={others.length === 0 ? 'Других регионов нет' : undefined}
              onClick={() => setMovingCar(c.id)}>
              Перевести
            </button>
          )}
        </div>
      ))}
    </section>
  )
}
